import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CallCenterOrder, CallCenterOrderListResponse } from '@matix/types';
import { useCurrency } from '../print/useCurrency';
import { callCenterApi, callCenterStreamUrl, ccErrorMessage } from './api';
import { useCallCenter } from './store';

/**
 * A clock that ticks once a second, anchored to the SERVER's time.
 *
 * The dashboard's headline column is a live waiting duration, and the machines
 * it renders on are cheap counter tablets and call-centre PCs whose clocks are
 * routinely minutes out. Anchoring to `Date.now()` shows an order as five
 * minutes late the instant it is placed, which trains staff to ignore the
 * colour bands — and a colour band nobody reads is worse than no colour band,
 * because it looks like the system is watching when it is not.
 *
 * So: take the server's clock from the response, remember the local instant it
 * landed, and derive "now on the server" from the two. Exactly the trick the
 * KDS boards use.
 */
export function useServerClock(serverTime: string | null | undefined): () => number {
  const anchor = useRef<{ server: number; local: number } | null>(null);
  const [, force] = useState(0);

  useEffect(() => {
    if (!serverTime) return;
    const parsed = Date.parse(serverTime);
    if (Number.isFinite(parsed)) anchor.current = { server: parsed, local: Date.now() };
  }, [serverTime]);

  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  return useCallback(() => {
    const a = anchor.current;
    // No response has landed yet — the local clock is all there is, and it is
    // only used to advance a duration that is about to be corrected anyway.
    if (!a) return Date.now();
    return a.server + (Date.now() - a.local);
  }, []);
}

/** Seconds an order has been open, on the server's clock. */
export function elapsedSeconds(order: CallCenterOrder, nowMs: number): number {
  const placed = Date.parse(order.placedAt);
  if (!Number.isFinite(placed)) return 0;
  // A terminal order's clock stops when it stopped, not when the page loaded.
  const end =
    order.status === 'delivered' && order.deliveredAt
      ? Date.parse(order.deliveredAt)
      : order.status === 'cancelled' && order.cancelledAt
        ? Date.parse(order.cancelledAt)
        : nowMs;
  return Math.max(0, Math.floor((end - placed) / 1000));
}

/**
 * `12.50 KWD`, at the tenant's currency precision.
 *
 * THE CALL-CENTRE BOOTSTRAP IS THE SOURCE, not the till's PrintConfig.
 *
 * `useCurrency()` reads the PrintConfig query cache, and that query is mounted
 * inside `LegacyApp` — which the agent surface replaces outright. So on the
 * agent screen the cache was always empty and every amount rendered through
 * `FALLBACK_CURRENCY`: a KWD tenant priced its phone orders in "EGP", at two
 * decimals instead of three. Same shape of trap as the missing ToastHost.
 *
 * `/call-center/bootstrap` already carries the resolved base currency and is
 * already fetched for EVERY signed-in user (it is what decides whether a till
 * shows a Pending Orders tab), so this needs no extra request and cannot go
 * stale against the totals — the same bootstrap prices the draft.
 *
 * PrintConfig stays as the fallback for the branch-side screens inside the
 * till, where it is warm and is what the rest of that shell formats with.
 */
export function useMoneyFormatter(): (n: number) => string {
  const printConfigCurrency = useCurrency();
  const bootstrapCurrency = useCallCenter((s) => s.bootstrap?.currency);
  const code = bootstrapCurrency?.code ?? printConfigCurrency.code;
  const precision = bootstrapCurrency?.precision ?? printConfigCurrency.precision;
  const digits = Math.max(0, Math.min(8, precision));
  return useCallback(
    (n: number) => `${(Number.isFinite(n) ? n : 0).toFixed(digits)} ${code}`,
    [digits, code],
  );
}

interface LiveOrdersOptions {
  mode: 'branch' | 'all';
  /** Extra query params. Changing these refetches. */
  params?: Record<string, string | number | boolean | undefined>;
  pollSeconds?: number;
  enabled?: boolean;
}

interface LiveOrdersResult {
  orders: CallCenterOrder[];
  total: number;
  serverTime: string | null;
  warnSeconds: number;
  criticalSeconds: number;
  loading: boolean;
  error: string | null;
  /** True while the SSE connection is up. Screens show a "live" dot. */
  live: boolean;
  refresh: () => void;
}

/**
 * One order list, kept live.
 *
 * BOTH transports, on purpose. The SSE stream is the fast path — a kitchen
 * bump moves the row on the branch screen in well under a second. The poll is
 * the honest fallback: this app is deployed behind IIS/ARR on one target and
 * nginx on another, tablets sleep, and the one thing worse than a screen that
 * refreshes every fifteen seconds is a screen that stopped refreshing an hour
 * ago and looks fine.
 *
 * The poll is NOT disabled when the stream connects. It runs slower, but it
 * runs — a stream can be alive at the socket level and still have missed a
 * message, and the poll is what makes that self-correcting rather than
 * permanent.
 */
export function useLiveOrders(opts: LiveOrdersOptions): LiveOrdersResult {
  const { mode, params, pollSeconds = 15, enabled = true } = opts;

  const [state, setState] = useState<CallCenterOrderListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState(false);

  // Serialised so the effect below depends on the VALUE of the params, not on
  // the identity of an object literal the caller rebuilds every render.
  const paramsKey = JSON.stringify(params ?? {});

  const fetchOnce = useCallback(async () => {
    try {
      const parsed = JSON.parse(paramsKey) as Record<string, never>;
      const data =
        mode === 'all'
          ? await callCenterApi.agent.list(parsed)
          : await callCenterApi.branch.pending(parsed);
      setState(data);
      setError(null);
    } catch (err) {
      setError(ccErrorMessage(err, 'Failed to load orders'));
    } finally {
      setLoading(false);
    }
  }, [mode, paramsKey]);

  useEffect(() => {
    if (!enabled) return;
    setLoading(true);
    void fetchOnce();
    const id = setInterval(() => void fetchOnce(), Math.max(5, pollSeconds) * 1000);
    return () => clearInterval(id);
  }, [enabled, fetchOnce, pollSeconds]);

  // ── The stream ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    const url = callCenterStreamUrl(mode);
    if (!url || typeof EventSource === 'undefined') return;

    let source: EventSource;
    try {
      source = new EventSource(url);
    } catch {
      return; // No stream; the poll above still runs.
    }

    source.onopen = () => setLive(true);
    source.onerror = () => setLive(false);
    source.onmessage = (event: MessageEvent<string>) => {
      try {
        // One literal per arm — `{ type: 'hello' | 'ping' }` as a single member
        // does not narrow away cleanly in the negative branch, and the switch
        // below reads better than the equality chain it replaces.
        const parsed = JSON.parse(event.data) as
          | { type: 'order'; order: CallCenterOrder; serverTime: string }
          | { type: 'removed'; orderId: number; serverTime: string }
          | { type: 'hello'; serverTime: string }
          | { type: 'ping'; serverTime: string };

        switch (parsed.type) {
          case 'hello':
          case 'ping': {
            const { serverTime } = parsed;
            setState((prev) => (prev ? { ...prev, serverTime } : prev));
            return;
          }
          case 'removed': {
            const { orderId, serverTime } = parsed;
            setState((prev) =>
              prev
                ? { ...prev, serverTime, orders: prev.orders.filter((o) => o.id !== orderId) }
                : prev,
            );
            return;
          }
          case 'order': {
            const { order: incoming, serverTime } = parsed;
            setState((prev) => {
              if (!prev) return prev;
              const known = prev.orders.some((o) => o.id === incoming.id);
              // A NEW order arriving on the stream is left to the poll rather
              // than spliced in here: the list has filters, a sort and a page
              // size that this handler does not know, and inserting a row the
              // filter excludes is a phantom the agent cannot dismiss.
              // Updating a row we are already showing has none of those
              // problems.
              if (!known) return prev;
              return {
                ...prev,
                serverTime,
                orders: prev.orders.map((o) => (o.id === incoming.id ? incoming : o)),
              };
            });
            return;
          }
        }
      } catch {
        /* a malformed frame is not worth breaking the screen over */
      }
    };

    return () => {
      setLive(false);
      source.close();
    };
  }, [enabled, mode]);

  return useMemo(
    () => ({
      orders: state?.orders ?? [],
      total: state?.total ?? 0,
      serverTime: state?.serverTime ?? null,
      warnSeconds: state?.warnSeconds ?? 1200,
      criticalSeconds: state?.criticalSeconds ?? 2400,
      loading,
      error,
      live,
      refresh: () => void fetchOnce(),
    }),
    [state, loading, error, live, fetchOnce],
  );
}
